import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../hooks/useToast';
import { awsAIAgentService, type AWSAIAgentRequest } from '../../services/AWSAIAgentService';
import { AIAnalysisCacheService } from '../../services/database';
import type { AIAnalysisCache } from '../../types/database';
import { formatMarkdownText } from '../../utils/markdownFormatter';
import { AIChatInterface, type ChatMessage } from './AIChatInterface';

interface AWSAIAnalysisProps {
    ticker: string;
    companyName: string;
    currentPrice: number;
    marketCap?: number;
    className?: string;
}

export const AWSAIAnalysis: React.FC<AWSAIAnalysisProps> = ({
    ticker,
    companyName,
    currentPrice,
    marketCap,
    className = '',
}) => {
    const [analysis, setAnalysis] = useState<AIAnalysisCache | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [showChat, setShowChat] = useState(false);
    const [showThinking, setShowThinking] = useState(false);
    const { showToast } = useToast();

    const loadCachedAnalysis = async () => {
        try {
            const cached = await AIAnalysisCacheService.getCachedAnalysis(ticker);
            if (cached) {
                setAnalysis(cached);
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error loading cached analysis:', err);
            return false;
        }
    };

    const generateFreshAnalysis = async (forceNewSession = false) => {
        setLoading(true);
        setError(null);

        try {
            // Reset session for fresh analysis if requested
            if (forceNewSession) {
                awsAIAgentService.resetSession();
            }

            const request: AWSAIAgentRequest = {
                prompt: `Analyze ${ticker} stock for 12 months with detailed analysis`,
                symbol: ticker,
                timeframe: "1y",
                analysis_depth: "detailed"
            };

            const response = await awsAIAgentService.invokeAgent(request);
            
            // Try to save to cache, but don't fail if caching fails
            try {
                const cachedAnalysis = await AIAnalysisCacheService.saveCachedAnalysis(
                    ticker, 
                    response.result, 
                    24 // Cache for 24 hours
                );
                setAnalysis(cachedAnalysis);
                
                // Show success message with recommendation if available
                const rec = cachedAnalysis.analysis.recommendation;
                if (rec) {
                    showToast(`Analysis complete: ${rec} recommendation`, 'success');
                } else {
                    showToast('Analysis generated and cached successfully', 'success');
                }
            } catch (cacheError) {
                console.warn('Failed to cache analysis, but showing results anyway:', cacheError);
                // Create a temporary analysis object for display
                const tempAnalysis = {
                    id: 'temp-' + Date.now(),
                    ticker: ticker.toUpperCase(),
                    analysis: response.result,
                    created_at: new Date().toISOString(),
                    expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
                };
                setAnalysis(tempAnalysis);
                showToast('Analysis generated successfully (caching unavailable)', 'success');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to generate AI analysis';
            setError(errorMessage);
            showToast(errorMessage, 'error');
            console.error('AWS AI Analysis error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateNow = async () => {
        // Reset chat when generating new analysis
        setChatMessages([]);
        setShowChat(false);
        // Reset session for this specific ticker
        awsAIAgentService.resetSessionForTicker(ticker);
        await generateFreshAnalysis(true); // Force new session for manual generation
    };

    const handleSendChatMessage = async (message: string): Promise<string> => {
        try {
            // Use the same session ID for follow-up questions
            const response = await awsAIAgentService.sendFollowUpMessage(message, ticker);
            
            // Return the comprehensive analysis or any response text
            return response.result.comprehensive_analysis || 'No response received';
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
            throw new Error(errorMessage);
        }
    };

    useEffect(() => {
        const initializeAnalysis = async () => {
            setLoading(true);
            const hasCached = await loadCachedAnalysis();
            if (!hasCached) {
                await generateFreshAnalysis();
            } else {
                // If we loaded from cache, we need to ensure a session exists for chat
                // Generate a new session if one doesn't exist
                if (!awsAIAgentService.getSessionId()) {
                    console.log('Cached analysis loaded, but no active session. Creating new session for chat.');
                    // Session will be created on first chat message
                }
                setLoading(false);
            }
        };

        initializeAnalysis();
    }, [ticker]);

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 1) {
            return 'Just now';
        } else if (diffInHours < 24) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        }
    };

    // Parse the comprehensive analysis to separate thinking and content
    const parseAnalysis = (text: string) => {
        console.log('=== PARSING ANALYSIS ===');
        console.log('Full text length:', text.length);
        console.log('First 500 chars:', text.substring(0, 500));
        
        // Check if response has </think> tag (AWS Agent format: thinking content comes before </think>)
        const thinkEndIndex = text.indexOf('</think>');
        
        if (thinkEndIndex !== -1) {
            // Split at </think> tag
            const thinking = text.substring(0, thinkEndIndex).trim();
            const content = text.substring(thinkEndIndex + 8).trim(); // 8 = length of '</think>'
            console.log('✓ </think> tag found at index:', thinkEndIndex);
            console.log('Thinking length:', thinking.length, 'Content length:', content.length);
            console.log('Content preview:', content.substring(0, 200));
            return { thinking, content };
        }
        
        // No think tags - treat entire response as content
        console.log('✗ No </think> tag found - showing full response as content');
        return { thinking: '', content: text.trim() };
    };

    // Extract price information from the analysis content
    const extractPriceInfo = (text: string) => {
        const priceInfo: {
            recommendation?: string;
            confidenceLevel?: number;
            timeframe?: string;
            currentPrice?: number;
            targetPrice?: number;
            stopLoss?: number;
            upsidePotential?: number;
            downsideRisk?: number;
        } = {};

        // Extract FINAL RECOMMENDATION (handles markdown ** and plain text)
        const recMatch = text.match(/\*{0,2}FINAL RECOMMENDATION:\*{0,2}\s*(\w+)/i);
        if (recMatch) priceInfo.recommendation = recMatch[1].toUpperCase();

        // Extract CONFIDENCE LEVEL (handles markdown ** and plain text)
        const confMatch = text.match(/\*{0,2}CONFIDENCE LEVEL:\*{0,2}\s*(\d+)%/i);
        if (confMatch) priceInfo.confidenceLevel = parseInt(confMatch[1]);

        // Extract TIMEFRAME (handles markdown ** and plain text)
        const timeMatch = text.match(/\*{0,2}TIMEFRAME:\*{0,2}\s*(\w+)/i);
        if (timeMatch) priceInfo.timeframe = timeMatch[1];

        // Extract Current Price (handles markdown **, bullets •/-, and plain text)
        const currentMatch = text.match(/[-•]\s*\*{0,2}Current Price:\*{0,2}\s*\$?([\d,]+\.?\d*)/i);
        if (currentMatch) priceInfo.currentPrice = parseFloat(currentMatch[1].replace(/,/g, ''));

        // Extract Target Price (handles markdown **, bullets •/-, and plain text)
        const targetMatch = text.match(/[-•]\s*\*{0,2}Target Price[^:]*:\*{0,2}\s*\$?([\d,]+\.?\d*)/i);
        if (targetMatch) priceInfo.targetPrice = parseFloat(targetMatch[1].replace(/,/g, ''));

        // Extract Stop Loss (handles markdown **, bullets •/-, and plain text)
        const stopMatch = text.match(/[-•]\s*\*{0,2}Stop Loss:\*{0,2}\s*\$?([\d,]+\.?\d*)/i);
        if (stopMatch) priceInfo.stopLoss = parseFloat(stopMatch[1].replace(/,/g, ''));

        // Extract Upside Potential (handles markdown **, bullets •/-, and plain text)
        const upsideMatch = text.match(/[-•]\s*\*{0,2}Upside Potential:\*{0,2}\s*([\d.]+)%/i);
        if (upsideMatch) priceInfo.upsidePotential = parseFloat(upsideMatch[1]);

        // Extract Downside Risk (handles markdown **, bullets •/-, and plain text)
        const downMatch = text.match(/[-•]\s*\*{0,2}Downside Risk:\*{0,2}\s*([\d.]+)%/i);
        if (downMatch) priceInfo.downsideRisk = parseFloat(downMatch[1]);

        console.log('=== EXTRACTED PRICE INFO ===');
        console.log('Recommendation:', priceInfo.recommendation);
        console.log('Confidence Level:', priceInfo.confidenceLevel);
        console.log('Timeframe:', priceInfo.timeframe);
        console.log('Current Price:', priceInfo.currentPrice);
        console.log('Target Price:', priceInfo.targetPrice);
        console.log('Stop Loss:', priceInfo.stopLoss);
        console.log('Upside Potential:', priceInfo.upsidePotential);
        console.log('Downside Risk:', priceInfo.downsideRisk);
        console.log('========================');
        return priceInfo;
    };



    if (loading && !analysis) {
        return (
            <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
                <div className="flex items-center justify-center py-6">
                    <LoadingSpinner size="lg" />
                    <span className="ml-3 text-gray-600">Generating AWS AI analysis...</span>
                </div>
            </div>
        );
    }

    if (error && !analysis) {
        return (
            <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
                <div className="text-center py-6">
                    <div className="text-red-600 mb-4">
                        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm">{error}</p>
                    </div>
                    <button
                        onClick={handleGenerateNow}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!analysis) {
        return null;
    }

    return (
        <div className={`bg-white rounded-lg shadow-md ${className}`}>
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Enhanced AI Analysis</h3>
                    <div className="flex items-center space-x-3">
                        <div className="text-sm text-gray-500">
                            Generated {formatTimeAgo(analysis.created_at)}
                        </div>
                        <button
                            onClick={handleGenerateNow}
                            disabled={loading}
                            className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {loading ? 'Generating...' : 'Generate Now'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-6">
                {/* Comprehensive Analysis */}
                <div className="space-y-3">
                    {(() => {
                        const { thinking, content } = parseAnalysis(analysis.analysis.comprehensive_analysis);
                        
                        // Parse from text once
                        const parsedFromText = extractPriceInfo(content);
                        
                        // First try to use pre-extracted data from database, then fall back to parsed text
                        const extractedPriceInfo = {
                            recommendation: analysis.analysis.recommendation || parsedFromText.recommendation,
                            confidenceLevel: analysis.analysis.confidence_level || parsedFromText.confidenceLevel,
                            timeframe: parsedFromText.timeframe,
                            currentPrice: analysis.analysis.current_price || parsedFromText.currentPrice,
                            targetPrice: analysis.analysis.target_price || parsedFromText.targetPrice,
                            stopLoss: analysis.analysis.stop_loss || parsedFromText.stopLoss,
                            upsidePotential: analysis.analysis.upside_potential || parsedFromText.upsidePotential,
                            downsideRisk: analysis.analysis.downside_risk || parsedFromText.downsideRisk,
                        };
                        
                        console.log('=== FINAL EXTRACTED INFO ===');
                        console.log('Using database fields:', {
                            recommendation: analysis.analysis.recommendation,
                            confidence_level: analysis.analysis.confidence_level,
                            current_price: analysis.analysis.current_price,
                            target_price: analysis.analysis.target_price,
                            stop_loss: analysis.analysis.stop_loss,
                            upside_potential: analysis.analysis.upside_potential,
                            downside_risk: analysis.analysis.downside_risk,
                        });
                        console.log('Final merged data:', extractedPriceInfo);
                        console.log('===========================');

                        return (
                            <>
                                {/* Thinking/Reasoning Section - Collapsible (Hidden by Default) */}
                                {thinking && (
                                    <div className="border border-purple-200 rounded-lg overflow-hidden bg-gradient-to-r from-purple-50 to-indigo-50">
                                        <button
                                            onClick={() => setShowThinking(!showThinking)}
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-100/50 transition-colors"
                                            aria-expanded={showThinking}
                                            aria-controls="thinking-content"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                    </svg>
                                                </div>
                                                <div className="text-left">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm font-semibold text-gray-900">View AI Thought Process</span>
                                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                                            Optional
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-600 mt-0.5">
                                                        {showThinking ? 'Hide internal reasoning steps' : 'See how the AI analyzed this stock'}
                                                    </p>
                                                </div>
                                            </div>
                                            <svg 
                                                className={`w-5 h-5 text-purple-600 transition-transform duration-200 ${showThinking ? 'rotate-180' : ''}`} 
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {showThinking && (
                                            <div 
                                                id="thinking-content"
                                                className="px-4 py-4 bg-white border-t border-purple-200 animate-fadeIn"
                                            >
                                                <div className="flex items-start space-x-2 mb-3 pb-3 border-b border-gray-200">
                                                    <svg className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-xs text-gray-600 leading-relaxed">
                                                        This section shows the AI's internal reasoning process. It includes the steps and considerations 
                                                        the model took before arriving at the final analysis below.
                                                    </p>
                                                </div>
                                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-mono bg-gray-50 p-4 rounded border border-gray-200 max-h-96 overflow-y-auto">
                                                    {thinking}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Enhanced Price Analysis Card - Using extracted data */}
                                {extractedPriceInfo.recommendation && (
                                    <div className={`rounded-lg p-4 border-2 ${
                                        extractedPriceInfo.recommendation === 'BUY' ? 'bg-green-50 border-green-300' :
                                        extractedPriceInfo.recommendation === 'SELL' ? 'bg-red-50 border-red-300' :
                                        'bg-yellow-50 border-yellow-300'
                                    }`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <span className="text-3xl">
                                                    {extractedPriceInfo.recommendation === 'BUY' ? '📈' :
                                                     extractedPriceInfo.recommendation === 'SELL' ? '📉' : '⏸️'}
                                                </span>
                                                <div>
                                                    <h4 className="text-xl font-bold text-gray-900">
                                                        {extractedPriceInfo.recommendation}
                                                    </h4>
                                                    {extractedPriceInfo.confidenceLevel && (
                                                        <p className="text-sm text-gray-600">
                                                            Confidence: {extractedPriceInfo.confidenceLevel}%
                                                        </p>
                                                    )}
                                                    {extractedPriceInfo.timeframe && (
                                                        <p className="text-xs text-gray-500">
                                                            Timeframe: {extractedPriceInfo.timeframe}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Price Targets Grid - Using extracted data */}
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
                                            {extractedPriceInfo.currentPrice && (
                                                <div className="bg-white rounded p-2">
                                                    <p className="text-xs text-gray-500 font-medium">Current Price</p>
                                                    <p className="text-lg font-bold text-gray-900">
                                                        ${extractedPriceInfo.currentPrice.toFixed(2)}
                                                    </p>
                                                </div>
                                            )}
                                            {extractedPriceInfo.targetPrice && (
                                                <div className="bg-white rounded p-2">
                                                    <p className="text-xs text-gray-500 font-medium">Target Price</p>
                                                    <p className="text-lg font-bold text-green-600">
                                                        ${extractedPriceInfo.targetPrice.toFixed(2)}
                                                    </p>
                                                </div>
                                            )}
                                            {extractedPriceInfo.stopLoss && (
                                                <div className="bg-white rounded p-2">
                                                    <p className="text-xs text-gray-500 font-medium">Stop Loss</p>
                                                    <p className="text-lg font-bold text-red-600">
                                                        ${extractedPriceInfo.stopLoss.toFixed(2)}
                                                    </p>
                                                </div>
                                            )}
                                            {extractedPriceInfo.upsidePotential !== undefined && (
                                                <div className="bg-white rounded p-2">
                                                    <p className="text-xs text-gray-500 font-medium">Upside Potential</p>
                                                    <p className="text-lg font-bold text-green-600">
                                                        +{extractedPriceInfo.upsidePotential.toFixed(1)}%
                                                    </p>
                                                </div>
                                            )}
                                            {extractedPriceInfo.downsideRisk !== undefined && (
                                                <div className="bg-white rounded p-2">
                                                    <p className="text-xs text-gray-500 font-medium">Downside Risk</p>
                                                    <p className="text-lg font-bold text-red-600">
                                                        -{extractedPriceInfo.downsideRisk.toFixed(1)}%
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Main Analysis Content - Always Visible */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-900 flex items-center mb-3">
                                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Comprehensive Analysis
                                    </h4>
                                    <div className="text-sm leading-relaxed bg-gray-50 p-6 rounded-lg overflow-x-auto">
                                        {formatMarkdownText(content)}
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>

                {/* Analysis Metadata */}
                <div className="border-t border-gray-200 pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                            <p className="text-gray-500 font-medium">Timeframe</p>
                            <p className="text-gray-900">{analysis.analysis.timeframe}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Analysis Depth</p>
                            <p className="text-gray-900 capitalize">{analysis.analysis.analysis_depth}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Model Confidence</p>
                            <p className="text-gray-900">{(analysis.analysis.confidence * 100).toFixed(0)}%</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Processing Time</p>
                            <p className="text-gray-900">{(analysis.analysis.processing_time_ms / 1000).toFixed(1)}s</p>
                        </div>
                        <div>
                            <p className="text-gray-500 font-medium">Session ID</p>
                            <p className="text-gray-900 text-xs font-mono">
                                {awsAIAgentService.getSessionId()?.split('-').pop() || 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Chat Interface Toggle */}
                <div className="border-t border-gray-200 pt-4">
                    <button
                        onClick={() => setShowChat(!showChat)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg transition-colors border border-blue-200"
                    >
                        <div className="flex items-center space-x-3">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <div className="text-left">
                                <p className="text-sm font-semibold text-gray-900">Ask Follow-up Questions</p>
                                <p className="text-xs text-gray-600">Continue the conversation with the AI agent</p>
                            </div>
                        </div>
                        <svg 
                            className={`w-5 h-5 text-gray-600 transition-transform ${showChat ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>

                {/* Chat Interface */}
                {showChat && (
                    <div className="border-t border-gray-200">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-200">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <h4 className="text-sm font-semibold text-gray-900">AI Conversation Active</h4>
                                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                            Session Active
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-700 mb-1">
                                        The AI agent remembers the analysis above and can answer follow-up questions about {ticker}.
                                    </p>
                                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                        </svg>
                                        <span className="font-mono">Session: ...{awsAIAgentService.getSessionId()?.split('-').pop() || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="h-96">
                            <AIChatInterface
                                onSendMessage={handleSendChatMessage}
                                initialMessages={chatMessages}
                                placeholder={`Ask about ${ticker} analysis...`}
                                className="h-full"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Disclaimer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-gray-600">
                        <p className="font-medium mb-1">Important Disclaimer</p>
                        <p>
                            This AI-generated analysis is powered by AWS AI Agent and is for informational purposes only.
                            Not financial advice. Always conduct your own research and consult qualified financial advisors before making investment decisions.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};